// Lapisan "kota hidup" ambien — awan halus melintas DI ATAS kota (+ bayangan lembut di tanah),
// kawanan burung kecil, dan ikan kecil yang melompat di sungai.
// Semua posisi dihitung deterministik dari waktu animasi `t` (bisa dijeda dari editor) dalam
// ruang iso-px zoom-1 relatif origin kamera, sehingga ikut geser/zoom peta dengan benar.
import { TILE_W, TILE_H } from "./iso";
import { hashN } from "./sky";

const SPRITE_W = 320, SPRITE_H = 160;
const CLOUD_PX = 2.7;   // 1 px sprite = 2.7 iso-px (awan ≈ 650–1500 iso-px ≈ 5–12 tile)
const MARGIN = 420;     // awan boleh melewati tepi pulau sejauh ini (iso-px)

export function createAmbient(c) {
  // c = { gridToScreen, origin, cam, gridSize, get atmos }
  const G = c.gridSize;
  const HALF_W = (G * TILE_W) / 2, FULL_H = G * TILE_H;
  // setengah lebar berlian pulau pada titik tanah y (iso-px)
  const halfW = y => HALF_W * Math.max(0, 1 - Math.abs(y - FULL_H / 2) / (FULL_H / 2));

  // ---------- AWAN HALUS ----------
  const CLOUDS = Array.from({ length: 18 }, (_, i) => ({
    u0: hashN(i * 7.13), fy: 0.08 + hashN(i * 3.31) * 0.84,
    alt: 420 + hashN(i * 9.7) * 380, s: 0.7 + hashN(i * 5.9) * 0.9,
    v: 11 + hashN(i * 4.3) * 13, shape: i % 4, a: 0.78 + hashN(i * 8.1) * 0.22, ph: hashN(i * 2.2) * 6
  }));
  // statistik frame terakhir (uji otomatis): jumlah entitas yang benar-benar digambar + sampel posisi
  const stats = { clouds: 0, birds: 0, fish: 0, cloudPos: [], birdPos: [], fishPos: [] };
  const sprites = [];
  function cloudSprite(k) {
    if (sprites[k]) return sprites[k];
    const cv = document.createElement("canvas"); cv.width = SPRITE_W; cv.height = SPRITE_H;
    const g = cv.getContext("2d");
    // Banyak gumpalan kecil tak beraturan (ukuran, tinggi & kepipihan acak) dengan gradien
    // radial yang DI-SQUASH bersama bentuknya (scale sebelum arc) sehingga tepi memudar
    // merata — tidak ada garis oval yang terlihat.
    const n = 11 + Math.floor(hashN(k * 1.3 + 0.7) * 5);
    for (let j = 0; j < n; j++) {
      const u = (j + 0.5) / n;
      const env = 1 - Math.pow(Math.abs(u - 0.5) * 2, 1.6); // tepi kiri/kanan mengecil
      const px = 28 + u * 264 + (hashN(k * 7 + j * 3.1) - 0.5) * 46;
      const r = (16 + hashN(k * 2.7 + j * 5.3) * 30) * (0.45 + 0.55 * env);
      const sq = 0.5 + hashN(k * 3.9 + j * 1.7) * 0.3; // kepipihan per gumpalan
      const py = 100 - hashN(k * 4.1 + j * 2.9) * 40 * env - r * 0.2;
      const rg = g.createRadialGradient(0, 0, 0, 0, 0, r);
      rg.addColorStop(0, "rgba(255,255,255,0.72)");
      rg.addColorStop(0.45, "rgba(255,255,255,0.42)");
      rg.addColorStop(0.78, "rgba(255,255,255,0.12)");
      rg.addColorStop(1, "rgba(255,255,255,0)");
      g.save();
      g.translate(px, py); g.scale(1, sq);
      g.fillStyle = rg;
      g.beginPath(); g.arc(0, 0, r, 0, 7); g.fill();
      g.restore();
    }
    // dasar awan dipipihkan lembut (fade bertahap, bukan potongan rata)
    const fade = g.createLinearGradient(0, 88, 0, 150);
    fade.addColorStop(0, "rgba(0,0,0,0)"); fade.addColorStop(0.5, "rgba(0,0,0,0.55)"); fade.addColorStop(1, "rgba(0,0,0,1)");
    g.globalCompositeOperation = "destination-out"; g.fillStyle = fade; g.fillRect(0, 88, SPRITE_W, 72);
    // sprite bayangan: siluet yang sama diwarnai gelap
    const sh = document.createElement("canvas"); sh.width = SPRITE_W; sh.height = SPRITE_H;
    const g2 = sh.getContext("2d");
    g2.drawImage(cv, 0, 0); g2.globalCompositeOperation = "source-in"; g2.fillStyle = "#0f2418"; g2.fillRect(0, 0, SPRITE_W, SPRITE_H);
    sprites[k] = { cloud: cv, shadow: sh };
    return sprites[k];
  }
  // posisi layar awan ke-i pada waktu t: { gx, gy (titik tanah), w, h (ukuran sprite di layar) }
  function cloudAt(cl, t) {
    const o = c.origin(), z = c.cam.zoom;
    const yg = FULL_H * cl.fy;
    const span = 2 * (halfW(yg) + MARGIN);
    const x = ((cl.u0 * span + cl.v * t) % span) - span / 2;
    const w = SPRITE_W * CLOUD_PX * cl.s * z, h = SPRITE_H * CLOUD_PX * cl.s * z;
    return { gx: o.x + x * z, gy: o.y + yg * z + Math.sin(t * 0.25 + cl.ph) * 3 * z, w: Math.max(26, w), h: Math.max(13, h) };
  }
  // Bayangan awan di tanah — di-clip ke berlian pulau agar tidak menodai langit.
  function drawShadows(ctx, t, W, H) {
    const dark = c.atmos.dark, rain = c.atmos.rain;
    const a = 0.09 * (1 - dark * 0.7) * (1 - rain * 0.5);
    if (a < 0.01) return;
    const T = c.gridToScreen(0, 0), R = c.gridToScreen(G, 0), B = c.gridToScreen(G, G), L = c.gridToScreen(0, G);
    ctx.save();
    ctx.beginPath(); ctx.moveTo(...T); ctx.lineTo(...R); ctx.lineTo(...B); ctx.lineTo(...L); ctx.closePath(); ctx.clip();
    ctx.globalAlpha = a;
    for (const cl of CLOUDS) {
      const p = cloudAt(cl, t);
      const sw = p.w * 1.05, sh = p.h * 0.5; // pipih di tanah (iso)
      const sx = p.gx + p.w * 0.08, sy = p.gy;
      if (sx + sw / 2 < 0 || sx - sw / 2 > W || sy + sh < 0 || sy - sh > H) continue;
      ctx.drawImage(cloudSprite(cl.shape).shadow, sx - sw / 2, sy - sh * 0.6, sw, sh);
    }
    ctx.restore();
  }
  function drawClouds(ctx, t, W, H) {
    const dark = c.atmos.dark, rain = c.atmos.rain, z = c.cam.zoom;
    const base = (1 - dark * 0.4) * (1 - rain * 0.25);
    stats.clouds = 0; stats.cloudPos = [];
    ctx.save();
    for (const cl of CLOUDS) {
      const p = cloudAt(cl, t);
      const y = p.gy - cl.alt * z;
      if (p.gx + p.w / 2 < 0 || p.gx - p.w / 2 > W || y + p.h < 0 || y - p.h > H) continue;
      ctx.globalAlpha = cl.a * base;
      ctx.drawImage(cloudSprite(cl.shape).cloud, p.gx - p.w / 2, y - p.h * 0.6, p.w, p.h);
      stats.clouds++; stats.cloudPos.push([Math.round(p.gx), Math.round(y)]);
    }
    ctx.restore();
  }

  // ---------- BURUNG KECIL ----------
  // 2 kawanan formasi V melintas bergantian arah (siklus 44/53 dtk, terbang 17 dtk) + 3 burung
  // berputar santai di atas pusat kota. Hilang saat malam / hujan deras.
  const FLOCKS = [{ cyc: 44, fly: 17, off: 6, n: 6, gap: 40 }, { cyc: 53, fly: 19, off: 31, n: 5, gap: 36 }];
  function drawBird(ctx, x, y, s, flap, a) {
    stats.birds++; if (stats.birdPos.length < 8) stats.birdPos.push([Math.round(x), Math.round(y)]);
    ctx.globalAlpha = a;
    ctx.beginPath();
    ctx.moveTo(x - s, y + flap * s * 0.55);
    ctx.quadraticCurveTo(x - s * 0.5, y - s * 0.3, x, y);
    ctx.quadraticCurveTo(x + s * 0.5, y - s * 0.3, x + s, y + flap * s * 0.55);
    ctx.stroke();
  }
  function drawBirds(ctx, t, W, H) {
    const dark = c.atmos.dark, rain = c.atmos.rain;
    const vis = Math.max(0, 1 - dark * 1.3) * (1 - rain * 0.75);
    stats.birds = 0; stats.birdPos = [];
    if (vis < 0.03) return;
    const o = c.origin(), z = c.cam.zoom;
    const s = Math.min(12, Math.max(1.7, 6 * z));
    ctx.save();
    ctx.strokeStyle = "#2a3341"; ctx.lineCap = "round";
    ctx.lineWidth = Math.max(0.8, s * 0.2);
    FLOCKS.forEach((f, fi) => {
      const tt = t + f.off, n = Math.floor(tt / f.cyc), ph = tt - n * f.cyc;
      if (ph >= f.fly) return;
      const p = ph / f.fly, dir = n % 2 ? -1 : 1;
      const yg = FULL_H * (0.2 + hashN(n * 3.3 + fi * 1.7) * 0.6);
      const reach = halfW(yg) + 320;
      const alt = 640 + hashN(n * 5.1 + fi) * 300 + Math.sin(p * Math.PI) * 140;
      const lx = (-reach + 2 * reach * p) * dir;
      const bx = o.x + lx * z, by = o.y + (yg - alt) * z;
      if (bx < -200 || bx > W + 200 || by < -100 || by > H + 100) return;
      const a = vis * Math.min(1, p * 8, (1 - p) * 8);
      for (let k = 0; k < f.n; k++) {
        const row = Math.ceil(k / 2), side = k % 2 ? 1 : -1;
        const x = bx - dir * row * f.gap * z;
        const y = by + side * row * f.gap * 0.42 * z + Math.sin(t * 2.1 + k) * 2.5 * z;
        drawBird(ctx, x, y, s, Math.sin(t * 7 + k * 0.9 + fi), a);
      }
    });
    // kelompok berputar
    const R = 520 + Math.sin(t * 0.11) * 140, alt2 = 700 + Math.sin(t * 0.17) * 60;
    for (let k = 0; k < 3; k++) {
      const ang = t * 0.22 + k * 0.55;
      const x = o.x + (HALF_W * 0.05 + Math.cos(ang) * R) * z;
      const y = o.y + (FULL_H * 0.48 + Math.sin(ang) * R * 0.45 - alt2) * z + Math.sin(t * 1.7 + k) * 2 * z;
      if (x < -40 || x > W + 40 || y < -40 || y > H + 40) continue;
      drawBird(ctx, x, y, s * 0.85, Math.sin(t * 6.5 + k * 1.3), vis * 0.9);
    }
    ctx.restore();
  }

  // ---------- IKAN MELOMPAT ----------
  let spots = [];
  let flowAxis = "x";
  function setWater(waterTiles, roadSet, axis) {
    flowAxis = axis;
    spots = waterTiles
      .filter(w => !roadSet.has(w.x + "," + w.y))
      .map(w => ({ w, h: hashN(w.x * 2.37 + w.y * 5.11 + 0.5) }))
      .filter(s => s.h < 0.09).slice(0, 26)
      .map(({ w, h }) => ({
        x: w.x + 0.5 + (hashN(w.x * 1.9 + w.y * 3.3) - 0.5) * 0.5,
        y: w.y + 0.5 + (hashN(w.x * 4.7 + w.y * 1.1) - 0.5) * 0.5,
        P: 7 + h * 110, off: hashN(w.x * 6.1 + w.y * 7.9) * 40
      }));
  }
  function drawFish(ctx, t, W, H) {
    const z = c.cam.zoom;
    stats.fish = 0; stats.fishPos = []; stats.fishSpots = spots.length;
    if (!spots.length || z < 0.15) return;
    const dark = c.atmos.dark;
    const hwz = (TILE_W / 2) * z, hhz = (TILE_H / 2) * z;
    // ikan melompat MELAWAN arus (hulu): sumbu x → kiri-atas, sumbu y → kanan-atas
    let ux = flowAxis === "x" ? -hwz : hwz, uy = -hhz;
    const n0 = Math.hypot(ux, uy) || 1; ux /= n0; uy /= n0;
    const DUR = 0.85, L = 0.3 * TILE_W * z, HJ = 0.14 * TILE_W * z;
    const fl = Math.max(2, 0.07 * TILE_W * z);
    ctx.save();
    ctx.lineCap = "round";
    for (const sp of spots) {
      const ph = (t + sp.off) % sp.P;
      if (ph >= DUR) continue;
      const p = ph / DUR;
      const [x0, y0] = c.gridToScreen(sp.x, sp.y);
      if (x0 < -60 || x0 > W + 60 || y0 < -60 || y0 > H + 60) continue;
      // cipratan masuk & keluar (cincin pipih mengembang)
      const ring = (cx, cy, q, aMul) => {
        const r = Math.max(1, q * 0.09 * TILE_W * z);
        ctx.globalAlpha = (1 - q) * (0.5 + dark * 0.3) * aMul;
        ctx.strokeStyle = "#F4FBFF"; ctx.lineWidth = Math.max(0.5, 0.9 * z);
        ctx.beginPath(); ctx.ellipse(cx, cy, r, r * 0.5, 0, 0, 7); ctx.stroke();
      };
      if (p < 0.3) ring(x0 - ux * L / 2, y0 - uy * L / 2, p / 0.3, 1);
      if (p > 0.7) ring(x0 + ux * L / 2, y0 + uy * L / 2, (p - 0.7) / 0.3, 1);
      // badan ikan di busur parabola
      const fx = x0 + ux * (p - 0.5) * L, fy = y0 + uy * (p - 0.5) * L - Math.sin(Math.PI * p) * HJ;
      stats.fish++; stats.fishPos.push([Math.round(fx), Math.round(fy), +p.toFixed(2)]);
      const tx = ux * L, ty = uy * L - Math.PI * Math.cos(Math.PI * p) * HJ; // tangen busur
      const ang = Math.atan2(ty, tx);
      ctx.globalAlpha = 1;
      ctx.save();
      ctx.translate(fx, fy); ctx.rotate(ang);
      ctx.fillStyle = dark > 0.4 ? "#E6F3FF" : "#CFE5F8";
      ctx.beginPath(); ctx.ellipse(0, 0, fl / 2, fl / 5, 0, 0, 7); ctx.fill();
      ctx.fillStyle = "#5C8FC2";
      ctx.beginPath(); ctx.ellipse(0, -fl / 12, fl / 2.2, fl / 9, 0, Math.PI, 0); ctx.fill(); // punggung
      ctx.beginPath(); ctx.moveTo(-fl / 2, 0); ctx.lineTo(-fl * 0.72, -fl / 5); ctx.lineTo(-fl * 0.72, fl / 5); ctx.closePath(); ctx.fill(); // ekor
      // kilau: siang titik kecil, malam lebih terang (pantulan bulan/lampu)
      ctx.fillStyle = `rgba(255,255,255,${(0.5 + dark * 0.5).toFixed(2)})`;
      ctx.beginPath(); ctx.arc(fl * 0.18, -fl / 14, Math.max(0.5, fl / 12), 0, 7); ctx.fill();
      ctx.restore();
    }
    ctx.restore();
  }

  return { setWater, drawShadows, drawFish, drawBirds, drawClouds, stats };
}
